import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Check, X, Users, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '../utils/network.js';
import { localizeMessage } from '../utils/toast.js';

function TeamInviteConfirm() {
  const { t } = useTranslation();
  const { token } = useParams();

  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [responding, setResponding] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null); // 'accepted' | 'declined'

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/organizations/team/invite/${token}`);
        const data = await res.json();
        if (res.ok && data.success) {
          setInvite(data);
        } else {
          setError(data.error || t('authPages.invalidInvite', 'This invitation link is invalid or has expired.'));
        }
      } catch {
        setError(t('authPages.invalidInvite', 'This invitation link is invalid or has expired.'));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token, t]);

  const respond = async (action) => {
    setResponding(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/organizations/team/invite/${token}/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setResult(data.status);
      } else {
        setError(data.error || t('authPages.inviteRespondFailed', 'Failed to record your response.'));
      }
    } catch {
      setError(t('authPages.inviteRespondFailed', 'Failed to record your response.'));
    } finally {
      setResponding(false);
    }
  };

  return (
    <section style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f7f0', padding: 20 }}>
      <div style={{ width: '100%', maxWidth: 440, background: 'white', borderRadius: 16, padding: '32px 28px', boxShadow: '0 4px 24px rgba(0,0,0,0.08)', textAlign: 'center' }}>
        {loading ? (
          <p style={{ color: '#666' }}>{t('authPages.loading', 'Loading...')}</p>
        ) : error && !invite ? (
          <>
            <AlertTriangle size={40} color="#e53935" style={{ marginBottom: 12 }} />
            <h2 style={{ fontSize: 20, fontWeight: 700, color: '#1a3d2e', margin: '0 0 8px' }}>{t('authPages.inviteInvalidTitle', 'Invitation not found')}</h2>
            <p style={{ fontSize: 14, color: '#666', margin: '0 0 20px' }}>{localizeMessage(error)}</p>
            <Link to="/" style={{ color: '#32614d', fontWeight: 600, textDecoration: 'none' }}>{t('authPages.backToHome', 'Back to Greenwich')}</Link>
          </>
        ) : result ? (
          <>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: result === 'accepted' ? '#e8f5e9' : '#f5f5f5', color: result === 'accepted' ? '#32614d' : '#888', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              {result === 'accepted' ? <Check size={26} /> : <X size={26} />}
            </div>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: '#1a3d2e', margin: '0 0 8px' }}>
              {result === 'accepted'
                ? t('authPages.inviteAcceptedTitle', "You're on the team!")
                : t('authPages.inviteDeclinedTitle', 'Invitation declined')}
            </h2>
            <p style={{ fontSize: 14, color: '#666', margin: 0 }}>
              {result === 'accepted'
                ? t('authPages.inviteAcceptedBody', `You've been confirmed as an authorized team member for ${invite.organizationName}.`)
                : t('authPages.inviteDeclinedBody', 'You will not be added to this organization\'s team.')}
            </p>
          </>
        ) : (
          <>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#e8f5e9', color: '#32614d', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <Users size={26} />
            </div>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: '#1a3d2e', margin: '0 0 6px' }}>
              {t('authPages.inviteTitle', 'Team invitation')}
            </h2>
            <p style={{ fontSize: 14, color: '#555', margin: '0 0 4px' }}>
              <strong>{invite.organizationName}</strong>{' '}
              {t('authPages.inviteBody', 'has listed you as an authorized team member')}
              {invite.role ? ` (${invite.role})` : ''}.
            </p>
            <p style={{ fontSize: 13, color: '#999', margin: '0 0 22px' }}>{invite.email}</p>

            {error && (
              <div style={{ background: '#ffebee', color: '#c62828', padding: '10px 14px', borderRadius: 10, fontSize: 12, border: '1px solid #ffcdd2', marginBottom: 14 }}>
                {localizeMessage(error)}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => respond('decline')} disabled={responding}
                style={{ flex: 1, padding: 12, borderRadius: 10, background: 'white', color: '#555', border: '1.5px solid #ddd', fontSize: 14, fontWeight: 700, cursor: responding ? 'not-allowed' : 'pointer' }}>
                {t('authPages.decline', 'Decline')}
              </button>
              <button onClick={() => respond('accept')} disabled={responding}
                style={{ flex: 1, padding: 12, borderRadius: 10, background: responding ? '#ccc' : 'linear-gradient(135deg,#32614d,#2a5240)', color: 'white', border: 'none', fontSize: 14, fontWeight: 700, cursor: responding ? 'not-allowed' : 'pointer' }}>
                {t('authPages.accept', 'Accept')}
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

export default TeamInviteConfirm;
